import { useEffect, Fragment } from 'react';
import { Transition } from '@headlessui/react';
import Image from 'next/future/image';
import Link from 'next/link';
import RemoveFromWishlist from '@/public/icons/product/remove-wishlist.svg';
import AddToWishlist from '@/public/icons/product/add-wishlist.svg';
import { X } from '@/public/icons/account/icons';

type WishlistNotificationT = {
  showNotification: boolean;
  setShowNotification: (val: boolean) => void;
  type: 'removed' | 'added';
  image: string;
  addToWishlist: () => void;
};

const WishlistNotification = ({
  showNotification,
  setShowNotification,
  type,
  image,
  addToWishlist,
}: Readonly<WishlistNotificationT>) => {
  useEffect(() => {
    if (showNotification) {
      const timeoutId = setTimeout(() => {
        setShowNotification(false);
      }, 5000);

      return () => {
        clearTimeout(timeoutId);
      };
    }

    return () => null;
  }, [showNotification, type]);

  const isRemoved = type === 'removed';

  return (
    <>
      {/* Global notification live region, render this permanently at the end of the document */}
      <div
        aria-live="assertive"
        className="pointer-events-none fixed bottom-[65px] right-0 z-50 flex w-full items-end px-4 py-6 text-xs sm:bottom-0 sm:top-[110px] sm:w-auto sm:items-start sm:p-6"
        // eslint-disable-next-line react/forbid-dom-props
        style={{ zIndex: 999 }}
      >
        <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
          {/* Notification panel, dynamically insert this into the live region when it needs to be displayed */}
          <Transition
            show={showNotification}
            as={Fragment}
            enter="transform ease-out duration-300 transition"
            enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
            enterTo="translate-y-0 opacity-100 sm:translate-x-0"
            leave="transition ease-in duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="pointer-events-auto w-full overflow-hidden rounded-lg bg-gray-200 p-3 shadow-md">
              <div className="flex items-center justify-between gap-10">
                <div className="flex items-center">
                  <div className="relative mr-2 h-[45px] shrink-0">
                    <Image
                      src={image}
                      width={100}
                      height={100}
                      alt="Wishlisted Item"
                      className="h-full w-auto object-contain group-hover:hidden"
                    />
                    <div className="absolute right-[-6px] bottom-[-6px] h-[20px] w-[20px]">
                      <Image
                        src={isRemoved ? RemoveFromWishlist : AddToWishlist}
                        width={20}
                        height={20}
                        alt="Add to Wishlist"
                        className="h-full w-full object-contain group-hover:hidden"
                      />
                    </div>
                  </div>
                  <p className="font-medium text-gray-700">
                    {isRemoved
                      ? 'Item removed from your list'
                      : 'Item added to your list'}
                  </p>
                </div>
                <div className="flex items-center gap-1 text-[#0D6EFD] underline">
                  {isRemoved ? (
                    <button type="button" onClick={() => addToWishlist()}>
                      Undo
                    </button>
                  ) : (
                    <Link href="/account/wishlist">
                      <button
                        type="button"
                        onClick={() => {
                          setShowNotification(false);
                        }}
                      >
                        See Wishlist
                      </button>
                    </Link>
                  )}
                  <button
                    type="button"
                    className="inline-flex rounded-md !bg-gray-200 p-2 text-gray-700/50 hover:text-gray-700 focus:outline-none"
                    onClick={() => {
                      setShowNotification(false);
                    }}
                  >
                    <span className="sr-only">Close</span>
                    <X className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </>
  );
};

export default WishlistNotification;
